import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AppRoutes } from "./Routes/AppRoutes";
import { Toaster, toast } from "react-hot-toast";
import {
  setupSocket,
  subscribeToUnifiedNotifications,
  notificationCount,
  subscribeToRaceReportUpdates,
} from "./services/socket";
import useSound from 'use-sound';
import { useNotification } from "./context/notficationContext";
import { TelesaleappRoutes } from "./Routes/TelasaleappRoutes";
import { LoginComponent } from "./Pages/LoginComponent";
import { useGetTelesalesCalendarTasksQuery } from "./features/telesaletaskSlice";
import { useGetCalenderQuery } from "./features/leadsSlice";
import { decryptData } from "./services/crypto";
import moment from 'moment';
import RaceReportPopup from "./Components/RaceReport/RaceReportPopup";

function App() {
  const [Notification, setNotification] = useState("");
  const [playSound] = useSound('/assets/notification.mp3');
  const { toggleNotificationTrue } = useNotification()
  const notifiedTasksRef = useRef(new Set()); // Track notified tasks globally

  // Check user role
  const [userRole, setUserRole] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  
  // Race popup state
  const [showRacePopup, setShowRacePopup] = useState(false);
  const [raceData, setRaceData] = useState([]);
  const [raceUpdateTime, setRaceUpdateTime] = useState(null);
  const lastRacePopupTime = useRef(null);
  
  useEffect(() => {
    const storedRole = localStorage.getItem("role");
    const storedId = localStorage.getItem("id");
    const hasToken = !!localStorage.getItem("token");
    
    if (storedRole && hasToken) {
      try {
        const role = decryptData(storedRole);
        setUserRole(role?.toLowerCase());
        setIsLoggedIn(true);
      } catch (error) {
        console.error("Failed to decrypt role:", error);
      }
    }
    
    if (storedId) {
      try {
        const userId = decryptData(storedId);
        setCurrentUserId(userId);
      } catch (error) {
        console.error("Failed to decrypt user ID:", error);
      }
    }
  }, []);

  const isTSAgent = userRole === 'ts';

  // Fetch calendar tasks based on user role
  // TS agents: fetch from telesales tasks
  const { data: tsTasks } = useGetTelesalesCalendarTasksQuery(undefined, {
    skip: !isTSAgent || !isLoggedIn,
    pollingInterval: 60000, // Check every 1 minute
  });

  // Regular employees: fetch from regular tasks
  const { data: regularTasks } = useGetCalenderQuery(undefined, {
    skip: isTSAgent || !isLoggedIn,
    pollingInterval: 60000, // Check every 1 minute
  });

  // Use appropriate tasks based on role
  const allTasks = isTSAgent ? tsTasks : regularTasks;

  // Global notification checker for today's tasks (ALL users)
  useEffect(() => {
    if (!isLoggedIn || !allTasks || allTasks.length === 0) {
      return;
    }
    
    const today = moment().startOf('day');
    const todayEnd = moment().endOf('day');
    
    allTasks.forEach(task => {
      if (task.nextTask?.deadline && task.nextTask?.nextTaskType) {
        const deadline = moment(task.nextTask.deadline);
        const isDueToday = deadline.isBetween(today, todayEnd, null, '[]');
        
        if (isDueToday) {
          const taskKey = `${task._id}-${deadline.format('YYYY-MM-DD')}`;
          
          if (!notifiedTasksRef.current.has(taskKey)) {
            const taskType = task.nextTask.nextTaskType === 'followUp' 
              ? 'Follow Up' 
              : task.nextTask.nextTaskType === 'meeting_schedule'
              ? 'Meeting'
              : task.nextTask.nextTaskType === 'meeting_reschedule'
              ? 'Rescheduled Meeting'
              : task.nextTask.nextTaskType === 'meetClient'
              ? 'Client Meeting'
              : task.nextTask.nextTaskType;
            
            const leadId = task.leadID?.leadID || task.leadID || 'N/A';
            const clientName = task.leadID?.clientID?.fullName || task.client?.fullName || 'N/A';
            const timeStr = deadline.format('hh:mm A');
            
            playSound();
            toast(
              `📅 Today's ${taskType}: Lead #${leadId} (${clientName}) at ${timeStr}`,
              {
                icon: '🔔',
                duration: 15000,
                style: {
                  background: '#2196F3',
                  color: '#fff',
                  fontWeight: '500',
                  fontSize: '0.95rem',
                }
              }
            );
            
            notifiedTasksRef.current.add(taskKey);
          }
        }
      }
    });
    
  }, [allTasks, isLoggedIn, userRole, playSound]);

  useEffect(() => {
    setupSocket()
  }, [])


  useEffect(() => {
    const unsubscribers = [
      subscribeToUnifiedNotifications((data) => {
        setNotification(data?.newMessage || data?.formattedMessage);
      }),
      notificationCount(() => {
        // Notification count updates handled in Header via API.
      }),
    ];

    return () => {
      unsubscribers.forEach((unsubscribe) => {
        if (typeof unsubscribe === "function") unsubscribe();
      });
    };
  }, [])

  // Subscribe to race report updates
  useEffect(() => {
    if (!isLoggedIn) return;

    const unsubscribeRace = subscribeToRaceReportUpdates((data) => {
      console.log('🏁 Race report update received:', data);
      
      // Prevent duplicate popups within the same hour
      const currentTime = moment().format('YYYY-MM-DD-HH');
      if (lastRacePopupTime.current === currentTime) {
        console.log('⏭️ Skipping duplicate race popup for this hour');
        return;
      }
      
      // Update race data
      if (data && data.employees && Array.isArray(data.employees)) {
        setRaceData(data.employees);
        setRaceUpdateTime(data.timestamp || new Date());
        setShowRacePopup(true);
        lastRacePopupTime.current = currentTime;
        
        // Optional: Play sound for race update
        // playSound();
      }
    });

    // Cleanup on unmount
    return () => {
      if (typeof unsubscribeRace === "function") unsubscribeRace();
    };
  }, [isLoggedIn]);



  useEffect(() => {
    if (Notification) {
      playSound()
      toast.success(Notification, {
        duration: 15000,
      })
      setNotification('')
    }
  }, [Notification, playSound])


  useEffect(() => {
    if (Notification) {
      toggleNotificationTrue()
    }
  }, [Notification, toggleNotificationTrue])


  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="login" element={<LoginComponent />} />
          <Route path="/telesale/*" element={<TelesaleappRoutes />} />
          <Route path="/*" element={<AppRoutes />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
      
      {/* Race Report Popup */}
      {isLoggedIn && (
        <RaceReportPopup
          isOpen={showRacePopup}
          onClose={() => setShowRacePopup(false)}
          raceData={raceData}
          currentTime={raceUpdateTime}
          currentUserId={currentUserId}
        />
      )}
    </>
  );
}

export default App;